import { useCallback, useEffect, useMemo, useRef } from 'react';
import { calculateStockMovementHash } from '../../../domain/inventory/inventoryDataHash';
import { cleanupDeletedExpiredProducts } from '../../../utils/expiredProductsManager';
import {
  orchestrateExpirySweep,
  applyExpirySweepCacheUpdates,
  applyExpirySweepStatePatches,
  createSkippedExpirySweepResult,
  EMPTY_SWEEP_RESULT
} from '../../../domain/operations/expirySweepOrchestrator';
import {
  EXPIRED_LOT_CHANGE_DEBOUNCE_MS,
  lotsHaveExpiredStock
} from '../../../domain/operations/expiredProductsLifecycle';
import { createDebouncedScheduler } from '../../../domain/operations/expirySweepScheduler';

/**
 * Expiry-Sweep: Guards, Orchestrator, Auto-Run beim Laden, Lot-Change-Debounce.
 * State-Setter bleiben in DataContext; Business-Logik liegt im Domain-Layer.
 */
export function useExpirySweepLifecycle({
  currentBusinessId,
  dataLoaded,
  products,
  lots,
  expiredProducts,
  setExpiredProducts,
  setProducts,
  setLots,
  setStockData
}) {
  const expirySweepInFlightRef = useRef(false);
  const expirySweepAutoRanForRef = useRef(null);
  const expiredProductsRef = useRef(expiredProducts);
  const lastLotSweepHashRef = useRef(null);
  const lotChangeSchedulerRef = useRef(null);
  const lotsMovementHash = useMemo(
    () => calculateStockMovementHash(lots, [], []),
    [lots]
  );

  if (!lotChangeSchedulerRef.current) {
    lotChangeSchedulerRef.current = createDebouncedScheduler(EXPIRED_LOT_CHANGE_DEBOUNCE_MS);
  }

  useEffect(() => {
    expiredProductsRef.current = expiredProducts;
  }, [expiredProducts]);

  useEffect(() => {
    expirySweepAutoRanForRef.current = null;
  }, [currentBusinessId]);

  const applyExpirySweepResult = useCallback(
    async (businessId, { force = false } = {}) => {
      const bid = businessId || currentBusinessId;
      if (!bid) {
        return { ...EMPTY_SWEEP_RESULT };
      }

      if (expirySweepInFlightRef.current) {
        return createSkippedExpirySweepResult().result;
      }

      if (!force && expirySweepAutoRanForRef.current === bid) {
        return createSkippedExpirySweepResult().result;
      }

      expirySweepInFlightRef.current = true;
      try {
        if (!force) {
          expirySweepAutoRanForRef.current = bid;
        }

        const orchestration = await orchestrateExpirySweep(bid, {
          previousExpiredProducts: expiredProductsRef.current
        });

        applyExpirySweepStatePatches(orchestration.patches, {
          setExpiredProducts,
          setProducts,
          setLots,
          setStockData
        });
        applyExpirySweepCacheUpdates(orchestration.cache);

        if (orchestration.resetAutoRunFlag) {
          expirySweepAutoRanForRef.current = null;
        }

        return orchestration.result;
      } finally {
        expirySweepInFlightRef.current = false;
      }
    },
    [currentBusinessId, setExpiredProducts, setProducts, setLots, setStockData]
  );

  useEffect(() => {
    if (!dataLoaded || !currentBusinessId || products.length === 0) return;
    cleanupDeletedExpiredProducts();
    void applyExpirySweepResult(currentBusinessId);
  }, [dataLoaded, currentBusinessId, products.length, applyExpirySweepResult]);

  const triggerExpiredProductsProcessing = useCallback(() => {
    if (products.length === 0 || !currentBusinessId) {
      return Promise.resolve({ processedCount: 0, derivedExpired: [] });
    }
    return applyExpirySweepResult(currentBusinessId, { force: true });
  }, [products.length, currentBusinessId, applyExpirySweepResult]);

  useEffect(() => {
    if (!dataLoaded || !currentBusinessId || !Array.isArray(lots) || lots.length === 0) {
      return undefined;
    }

    if (lastLotSweepHashRef.current === lotsMovementHash) {
      return undefined;
    }

    if (!lotsHaveExpiredStock(lots)) {
      lastLotSweepHashRef.current = lotsMovementHash;
      return undefined;
    }

    lotChangeSchedulerRef.current.schedule(() => {
      lastLotSweepHashRef.current = lotsMovementHash;
      void applyExpirySweepResult(currentBusinessId, { force: true });
    });

    return () => lotChangeSchedulerRef.current.cancel();
  }, [lotsMovementHash, dataLoaded, currentBusinessId, applyExpirySweepResult, lots]);

  return { triggerExpiredProductsProcessing };
}
